'use client';
import styles from './mainmenu.module.sass';
import cn from 'classnames';
import { LinkProps, LinkWrapper } from '@intigral/shared/components/LinkWrapper';
import { Breadcrumbs, FontIcon, ImageContainer, Lock, Skeleton } from '@intigral/shared';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@intigral/hooks/dom/useMediaQuery/useMediaQuery';
import { type ProvidersItem } from '@intigral/domain/lib/entity/interface/iGetProviders';
import { FontIconName } from '@intigral/shared/components/FontIcon';

interface DropdownItem {
    link: any;
    image?: string;
    isLockIcon?: boolean;
}

interface MainMenuItemProps extends LinkProps {
    item?: LinkProps;
    isActive?: boolean;
    icon?: FontIconName;
    activeIcon?: FontIconName;
    flag?: string;
    mobileStyle?: React.CSSProperties;
}

export interface MainMenuProps extends React.HTMLAttributes<HTMLDivElement> {
    nav: MainMenuItemProps[];
    dropdowns?: { [key: number]: DropdownItem[] } | null;
    dropLoading?: number | null; // Represents the index of the loading dropdown
    onDropdownItemClick?: () => void;
    breadcrumbPath?: LinkProps[];
    providersData?: ProvidersItem[];
    showBreadcrumbs?: boolean;
}

export const MainMenu = ({ nav, dropdowns = null, dropLoading, className, ...props }: MainMenuProps) => {
    const [visibleItems, setVisibleItems] = useState<number[]>([]);
    const [overflowItems, setOverflowItems] = useState<number[]>([]);
    const [isExpandVisible, setIsExpandVisible] = useState(false);

    const navRef = useRef<HTMLDivElement>(null);
    const expandRef = useRef<HTMLDivElement>(null);

    const isMobile = useMediaQuery('(max-width: 924px)');

    const toggleExpandVisibility = () => setIsExpandVisible((prev) => !prev);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                expandRef.current &&
                !expandRef.current.contains(event.target as Node) &&
                !navRef.current?.contains(event.target as Node)
            ) {
                setIsExpandVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDropdownToggle = (event: React.MouseEvent) => {
        event.stopPropagation();
        toggleExpandVisibility();
    };

    useLayoutEffect(() => {
        if (isMobile) return;

        const handleResize = () => {
            if (!navRef.current) return;

            const navWidth = navRef.current.clientWidth;
            const children = (Array.from(navRef.current.children) as HTMLElement[]).slice(0, nav.length);
            let totalWidth = 0;
            const newVisibleItems: number[] = [];
            const newOverflowItems: number[] = [];

            children.forEach((child, index) => {
                totalWidth += child.offsetWidth;
                if (totalWidth <= navWidth) {
                    newVisibleItems.push(index);
                } else {
                    newOverflowItems.push(index);
                }
            });

            setVisibleItems(newVisibleItems);
            setOverflowItems(newOverflowItems);
        };

        handleResize();

        const currentNavRef = navRef.current;
        const resizeObserver = new ResizeObserver(handleResize);

        if (currentNavRef) {
            resizeObserver.observe(currentNavRef);
            const menuItems = (Array.from(currentNavRef.children) as HTMLElement[]).slice(0, nav.length);
            menuItems.forEach((item) => resizeObserver.observe(item));
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver.disconnect();
        };
    }, [nav, isMobile]);

    return (
        <nav
            className={cn(styles.nav, className, isMobile && styles.mobile, 'mainmenu-wcp')}
            ref={navRef}
            {...(isExpandVisible && { style: { position: 'relative' } })}
            {...props}
        >
            {nav.map((item, index) => {
                if (!item) return null;

                const dropdownItems = dropdowns ? dropdowns[index] : null;
                const isVisible = visibleItems.includes(index);
                const isDropdownLoading = dropLoading === index;

                return (
                    <div
                        key={index}
                        className={cn(
                            styles.menuItem,
                            { [styles.hidden]: !isVisible && !isMobile },
                            { [styles.active]: item.isActive }
                        )}
                    >
                        <LinkWrapper
                            data-test-id={`tab_id_${index}`}
                            link={item}
                            style={isMobile ? item.mobileStyle : undefined}
                            {...(isExpandVisible && { onClick: toggleExpandVisibility })}
                            {...(!isMobile && isExpandVisible && { onMouseEnter: toggleExpandVisibility })}
                        >
                            {item.icon && (
                                <FontIcon
                                    name={(item.isActive && item.activeIcon) || item.icon}
                                    className={styles.icon}
                                    size="small"
                                />
                            )}
                            {item.text}
                            {item.flag && <span className={styles.flag}>{item.flag}</span>}
                        </LinkWrapper>
                        {dropdownItems && !dropLoading && (
                            <>
                                <div className={cn(styles.dropdown, isMobile && isExpandVisible && styles.visible)}>
                                    {dropdownItems.map((dropdownItem, dropdownIndex) => (
                                        <LinkWrapper
                                            link={dropdownItem.link}
                                            key={dropdownIndex}
                                            className={styles.dropdownItem}
                                            // onClick={toggleExpandVisibility}
                                            {...(isExpandVisible &&
                                                !dropdownItem?.isLockIcon && { onClick: toggleExpandVisibility })}
                                            disabled={dropdownItem?.isLockIcon}
                                        >
                                            {dropdownItem.image && (
                                                <ImageContainer
                                                    src={dropdownItem.image}
                                                    alt={dropdownItem.link.text}
                                                    ratio={1.5}
                                                    alignment="center"
                                                />
                                            )}
                                            {dropdownItem?.isLockIcon ? (
                                                <div className={styles.lock}>
                                                    <FontIcon name="lock" size="smaller" md="smaller" />
                                                </div>
                                            ) : null}
                                        </LinkWrapper>
                                    ))}
                                </div>
                                {isMobile && (
                                    <div className={styles.expandIcon} onClick={handleDropdownToggle}>
                                        <FontIcon
                                            name="chevron-left"
                                            size="smaller"
                                            {...(isExpandVisible ? { rotate: 'right' } : { rotate: 'left' })}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {isDropdownLoading && (
                            <div className={styles.dropdown}>
                                {Array.from({ length: 8 }).map((_, index) => (
                                    <Skeleton key={index} />
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
            {overflowItems.length > 0 && !isMobile && (
                <>
                    <div onClick={toggleExpandVisibility} className={styles.dots}>
                        <FontIcon name="more" size="small" md="small" />
                    </div>
                    {isExpandVisible && (
                        <div className={styles.expand} ref={expandRef}>
                            {overflowItems.map((index) => {
                                if (!nav[index]) return null;

                                const dropdownItems = dropdowns ? dropdowns[index] : null;

                                return (
                                    <div key={index} className={styles.menuItem}>
                                        <LinkWrapper
                                            link={nav[index]}
                                            onClick={toggleExpandVisibility}
                                            data-test-id={`dropdown_tab_id_${index}`}
                                        />
                                        {dropdownItems && (
                                            <div className={styles.dropdown}>
                                                {dropdownItems.map((dropdownItem, dropdownIndex) => (
                                                    <LinkWrapper
                                                        link={dropdownItem.link}
                                                        key={dropdownIndex}
                                                        className={styles.dropdownItem}
                                                        // onClick={toggleExpandVisibility}
                                                        disabled={dropdownItem?.isLockIcon}
                                                    >
                                                        {dropdownItem.image && (
                                                            <ImageContainer
                                                                src={dropdownItem.image}
                                                                alt={dropdownItem.link.text}
                                                                ratio={1.5}
                                                                alignment="center"
                                                            />
                                                        )}
                                                        {dropdownItem?.isLockIcon ? (
                                                            <div className={styles.lock}>
                                                                <FontIcon name="lock" size="x-small" md="x-small" />
                                                            </div>
                                                        ) : null}
                                                    </LinkWrapper>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </nav>
    );
};
